import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import Navbar from "../NavBar";
import Footer from "../Footer";
import Capabilities from "../Capabilities";
import Industries from "../industries";
import Insights from "../Insights";
import AboutUs from "../AboutUs";
import Search1 from "../Search";
import KeepExploring from "../KeepExploring";
import content from "../../content/content.json";

export default function InsightsSection() {
  const { insightsPage, shared } = content;
  const [active, setActive] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % insightsPage.slides.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [insightsPage.slides.length]);

  return (
    <section className="w-full bg-[#1b1d27] text-white overflow-hidden">
      <Navbar
        capabilities={<Capabilities />}
        industries={<Industries />}
        insights={<Insights />}
        about={<AboutUs />}
        search={<Search1 />}
      />

      <div className="relative w-full flex flex-col md:flex-row">
        {insightsPage.slides.map((item, index) => {
          const isActive = active === index;
          return (
            <motion.div
              key={`${item.type}-${index}`}
              className="relative transition-all duration-700 ease-in-out overflow-hidden cursor-pointer"
              style={{
                flex: isActive ? 1.5 : 1,
                height: "500px",
              }}
              onMouseEnter={() => setActive(index)}
            >
              <img
                src={item.image}
                alt={item.title}
                className={`w-full h-full object-cover transition-transform duration-700 ${
                  isActive ? "scale-105" : "scale-100"
                }`}
              />
              <div
                className={`absolute inset-0 transition-all duration-700 ${
                  isActive
                    ? "bg-black/50"
                    : "bg-gradient-to-t from-[#1b1d27] via-black/20 to-transparent"
                }`}
              ></div>

              {index === 0 && (
                <motion.h1
                  className="absolute top-8 left-8 text-6xl md:text-7xl font-bold"
                  initial={{ opacity: 0, y: -20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1 }}
                >
                  {insightsPage.heroTitle}
                </motion.h1>
              )}

              <motion.div
                className={`absolute bottom-0 left-0 w-full ${
                  isActive ? "bg-[#2a2c38]/90 border-t-4 border-red-500" : "bg-[#1b1d27]"
                } p-6 md:p-8`}
                animate={{ opacity: isActive ? 1 : 0.95 }}
                transition={{ duration: 0.5 }}
              >
                <p className="text-cyan-400 text-sm font-semibold uppercase">{item.type}</p>
                <h3 className="text-lg md:text-xl font-semibold mt-2 leading-snug">{item.title}</h3>
                {item.linkText && isActive && (
                  <a href="#" className="inline-block mt-3 text-white font-medium underline decoration-2 hover:opacity-80">
                    {item.linkText} {shared.arrow}
                  </a>
                )}
              </motion.div>
            </motion.div>
          );
        })}
      </div>

      <div className="bg-[#EFF6F7] text-gray-900 flex items-center justify-center px-6 py-12">
        <div className="bg-white shadow-sm rounded-lg w-full max-w-7xl p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <h2 className="text-2xl font-semibold">
            Explore <br className="md:hidden" /> {insightsPage.exploreBy}
          </h2>

          <div className="flex flex-col md:flex-row gap-6 flex-1">
            <div className="flex-1">
              <label className="text-gray-700 text-sm">{insightsPage.topic}</label>
              <div className="flex items-center border-b border-gray-300">
                <input type="text" className="w-full bg-transparent outline-none py-1 text-gray-800" readOnly />
                <ChevronDown className="text-red-500" />
              </div>
            </div>

            <div className="flex-1">
              <label className="text-gray-700 text-sm">{insightsPage.role}</label>
              <div className="flex items-center border-b border-gray-300">
                <input type="text" className="w-full bg-transparent outline-none py-1 text-gray-800" readOnly />
                <ChevronDown className="text-red-500" />
              </div>
            </div>

            <div className="flex-1">
              <label className="text-gray-700 text-sm">{insightsPage.industry}</label>
              <div className="flex items-center border-b border-gray-300">
                <input type="text" className="w-full bg-transparent outline-none py-1 text-gray-800" readOnly />
                <ChevronDown className="text-red-500" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#EFF6F7] text-gray-900 flex px-6 ">
        <p className="text-gray-500 font-medium text-5xl">{insightsPage.featuresHeading}</p>
      </div>

      <section className="bg-[#EFF6F7] text-gray-900 flex items-center justify-center px-6 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {insightsPage.featureItems.map((item) => (
            <div key={item.title} className="group cursor-pointer transition-all duration-300">
              <div className="overflow-hidden rounded-sm">
                <img
                  src={item.img}
                  alt={item.title}
                  className="w-full h-48 object-cover transform group-hover:scale-105 transition duration-500"
                />
              </div>
              <h3 className="text-[18px] md:text-[20px] font-medium text-gray-800 mt-3 group-hover:text-blue-700 transition-colors duration-300">
                {item.title}
              </h3>
            </div>
          ))}
        </div>
      </section>

      <section>
        <KeepExploring />
      </section>
      <Footer />
    </section>
  );
}

